import * as React from "react"
import { createRoot } from "react-dom/client"

import RegistrationPage from "./app/routes/accounts/registration_page"
import LoginPage from "./app/routes/accounts/login_page"
import {
  createBrowserRouter as createReactBrowserRouter,
  Outlet,
  RouterProvider,
} from "react-router-dom"
import Home from "./app/routes/home/home"
import Mosaic from "./app/routes/home/mosaic"
import CapturePage from "./app/routes/home/capture_page"
import SourcesPage from "./app/routes/home/sources_page"
import AccountPage from "./app/routes/home/account_page"
import { ProtectedRoute, LoggedOutRoute } from "./app/hooks/protected_route"
import { UserProvider } from "./app/hooks/auth"
import ErrorPage from "./error_page"
import { DeviceProvider } from "./app/providers/device_context"
import ForgotPasswordPage from "./app/routes/accounts/forgot_password_page"
import ResetPasswordPage from "./app/routes/accounts/reset_password_page"
import { FlashMessage, FlashProvider } from "./app/hooks/use_flash"
import KeyboardDetector from "./services/keyboard_detector"
import ProjectsPage from "./app/routes/home/projects_page"
import NowPage from "./app/routes/home/now_page"
import SourceReadingView from "./app/routes/home/source_reading_view"
import { NanaProvider } from "./app/hooks/useNana"
import { initSentry, Sentry } from "./services/sentry"
import { setupDesktopApp } from "./services/desktop"

const keyboardDetector = new KeyboardDetector()
const createBrowserRouter = Sentry.wrapCreateBrowserRouterV6(createReactBrowserRouter)

export default function setupApp() {
  initSentry()

  const router = createBrowserRouter([
    {
      path: "/app/register",
      element: (
        <UserProvider>
          <LoggedOutRoute>
            <RegistrationPage />
          </LoggedOutRoute>
        </UserProvider>
      ),
      errorElement: <ErrorPage />,
    },
    {
      path: "/app/login",
      element: (
        <UserProvider>
          <LoggedOutRoute>
            <LoginPage />
          </LoggedOutRoute>
        </UserProvider>
      ),
      errorElement: <ErrorPage />,
    },
    {
      path: "/app/forgot-password",
      element: (
        <UserProvider>
          <LoggedOutRoute>
            <ForgotPasswordPage />
          </LoggedOutRoute>
        </UserProvider>
      ),
      errorElement: <ErrorPage />,
    },
    {
      path: "/app/reset-password/:token",
      element: (
        <UserProvider>
          <LoggedOutRoute>
            <ResetPasswordPage />
          </LoggedOutRoute>
        </UserProvider>
      ),
      errorElement: <ErrorPage />,
    },
    {
      path: "/app",
      element: (
        <UserProvider>
          <ProtectedRoute>
            <NanaProvider>
              <Outlet />
            </NanaProvider>
          </ProtectedRoute>
        </UserProvider>
      ),
      children: [
        {
          path: "",
          element: (
            <Home>
              <Mosaic />
            </Home>
          ),
        },
        {
          path: "now",
          element: (
            <Home>
              <NowPage />
            </Home>
          ),
        },
        {
          path: "capture",
          element: (
            <Home>
              <CapturePage />
            </Home>
          ),
        },
        {
          path: "sources",
          element: (
            <Home>
              <SourcesPage />
            </Home>
          ),
        },
        {
          path: "projects",
          element: (
            <Home>
              <ProjectsPage />
            </Home>
          ),
        },
        {
          path: "account",
          element: (
            <Home>
              <AccountPage />
            </Home>
          ),
        },
        {
          path: "reading/:sourceUuid",
          element: <SourceReadingView />,
        },
      ],
      errorElement: <ErrorPage />,
    },
  ])

  setupDesktopApp((path) => router.navigate(path))

  createRoot(document.getElementById("app")).render(
    <DeviceProvider>
      <FlashProvider>
        <FlashMessage />
        <RouterProvider router={router} />
      </FlashProvider>
    </DeviceProvider>
  )
}
